import { CheckCircle2, XCircle, FileText, IdCard, Heart, Home, Briefcase, Stethoscope, Accessibility, Award, CreditCard, Clock, AlertCircle, Pencil, Archive, ArchiveRestore, Trash2, ShieldCheck } from "lucide-react";

// Liste des documents requis pour qu'un dossier soit considéré "complet" (présentable à un client).
// L'ordre est important : il définit l'affichage et le poids dans le score.
export const REQUIRED_DOCS = [
  { key: "cv", label: "CV", icon: FileText, required: true },
  // Une pièce d'identité valide est fournie soit en un seul document, soit
  // scindée recto/verso. Il faut également accepter la carte de séjour et ses
  // variantes recto/verso pour les candidats non-UE.
  { key: ["id_card_recto", "id_card_verso", "id_card", "carte_sejour_recto", "carte_sejour_verso", "carte_sejour"], label: "Pièce d'identité", icon: IdCard, required: true },
  { key: "attestation_secu", label: "Attestation Sécu", icon: Heart, required: true },
  { key: "justificatif_domicile", label: "Justificatif de domicile", icon: Home, required: true },
  { key: "certificat_travail", label: "Certificat travail / bulletins", icon: Briefcase, required: false },
  { key: "visite_medicale", label: "Visite médicale", icon: Stethoscope, required: true },
  { key: "rqth", label: "RQTH", icon: Accessibility, required: false },
  { key: "caces_habilitations", label: "CACES / habilitations", icon: Award, required: false },
  { key: "carte_btp", label: "Carte BTP", icon: IdCard, required: false },
  // Admin-only : tests sécurité (masqué côté candidat via filtre role).
  { key: "test_securite", label: "Tests sécurité", icon: ShieldCheck, required: false, adminOnly: true },
  { key: "rib", label: "RIB", icon: CreditCard, required: false },
];

/**
 * Compute compliance status from a list of doc types (strings).
 * Returns { provided, total, score, missing } where score is between 0 and 1.
 * `isAdmin` toggles inclusion of admin-only doc types (e.g. `test_securite`).
 */
export function computeCompliance(docTypes = [], role = "candidate", isAdmin = false) {
  const set = new Set(docTypes);
  const items = REQUIRED_DOCS.filter((d) => (!d.employeeOnly || role === "employee") && (!d.adminOnly || isAdmin));
  const required = items.filter((d) => d.required);
  const requiredProvided = required.filter((d) => Array.isArray(d.key) ? d.key.some((k) => set.has(k)) : set.has(d.key));
  const allProvided = items.filter((d) => Array.isArray(d.key) ? d.key.some((k) => set.has(k)) : set.has(d.key));
  return {
    provided: requiredProvided.length,
    total: required.length,
    providedAll: allProvided.length,
    totalAll: items.length,
    score: required.length > 0 ? requiredProvided.length / required.length : 1,
    missing: required.filter((d) => !(Array.isArray(d.key) ? d.key.some((k) => set.has(k)) : set.has(d.key))).map((d) => d.label),
  };
}

/**
 * Given the documents[] array (with validation_status) and a REQUIRED_DOCS entry,
 * return { provided, doc, status } where status is
 *   - "accepted", "rejected", "pending" (doc present)
 *   - "missing"                          (doc absent)
 */
export function statusForDocEntry(docs, entry) {
  const keys = Array.isArray(entry.key) ? entry.key : [entry.key];
  const matching = (docs || []).filter((d) => keys.includes(d.doc_type));
  if (matching.length === 0) return { provided: false, doc: null, status: "missing" };
  // Priorité : rejeté > en attente > accepté (pour signaler tout blocage)
  const rejected = matching.find((d) => d.validation_status === "rejected");
  if (rejected) return { provided: true, doc: rejected, status: "rejected" };
  const pending = matching.find((d) => !d.validation_status || d.validation_status === "pending");
  if (pending) return { provided: true, doc: pending, status: "pending" };
  return { provided: true, doc: matching[0], status: "accepted" };
}

const STATUS_STYLE = {
  accepted: { label: "Accepté", cls: "bg-green-100 text-green-800 border-green-300", Icon: CheckCircle2, iconCls: "text-green-600" },
  rejected: { label: "Refusé", cls: "bg-red-100 text-red-800 border-red-300", Icon: XCircle, iconCls: "text-red-600" },
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: Clock, iconCls: "text-amber-600" },
  missing: { label: "Manquant", cls: "bg-gray-100 text-gray-500 border-gray-200", Icon: AlertCircle, iconCls: "text-gray-300" },
};

/**
 * Inline badge to render in the list view.
 */
export function ComplianceBadge({ docTypes = [], role = "candidate" }) {
  const c = computeCompliance(docTypes, role);
  const pct = Math.round(c.score * 100);
  const color = pct === 100 ? "bg-green-100 text-green-800 border-green-300"
    : pct >= 60 ? "bg-amber-100 text-amber-800 border-amber-300"
    : "bg-red-100 text-red-800 border-red-300";
  return (
    <span
      title={c.missing.length ? `Manquant : ${c.missing.join(", ")}` : "Dossier complet"}
      className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-none ${color}`}
      data-testid="compliance-badge"
    >
      {c.provided}/{c.total} obl.
    </span>
  );
}

/**
 * Full checklist panel for the candidate detail dialog.
 * Now accepts a `documents` array (with validation_status) to render per-doc status.
 * Falls back to just presence-check if only `docTypes` is provided.
 *
 * Callbacks:
 * - onPreviewDoc(doc): fired when a tile with an existing doc is clicked
 *   → parent typically opens the split-preview panel.
 * - onRequestUpload(docType): fired when a tile with status "missing" is clicked
 *   → parent typically opens the file picker pre-filled with the doc_type.
 * - onRenameDoc(doc): OPTIONAL — when provided AND the tile has an existing doc,
 *   a small pencil icon is shown on the tile; clicking it fires this callback
 *   (the parent typically opens a rename dialog/prompt).
 * - headerAction: optional React node rendered next to the score in the header
 *   (typically an "Add document" button).
 */
export default function ComplianceChecklist({ docTypes = [], documents = null, role = "candidate", isAdmin = false, onPreviewDoc, onRequestUpload, onRenameDoc, onArchiveDoc, onDeleteDoc, headerAction = null }) {
  const useDocs = Array.isArray(documents);
  // Filter out archived docs from the visible set (they exist in DB but should not clutter the UI).
  // Archived docs remain accessible via the split-preview panel (archive tab, TBD) if needed.
  const visibleDocs = useDocs ? documents.filter((d) => !d.archived) : null;
  const set = new Set(useDocs ? (visibleDocs || []).map((d) => d.doc_type) : docTypes);
  const items = REQUIRED_DOCS.filter((d) => (!d.employeeOnly || role === "employee") && (!d.adminOnly || isAdmin));
  const c = computeCompliance(useDocs ? (visibleDocs || []).map((d) => d.doc_type) : docTypes, role, isAdmin);
  const pct = Math.round(c.score * 100);
  const barColor = pct === 100 ? "bg-green-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500";

  // Count status counters if documents provided
  const counts = { accepted: 0, rejected: 0, pending: 0, missing: 0 };
  if (useDocs) {
    items.forEach((entry) => {
      const st = statusForDocEntry(visibleDocs, entry).status;
      counts[st] = (counts[st] || 0) + 1;
    });
  }

  return (
    <div data-testid="compliance-checklist" className="bg-[#FAFAFA] border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-bold text-[#3F3F3F]">Conformité administrative</h4>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-2xl font-black text-[#3F3F3F] leading-none">{c.providedAll}<span className="text-sm text-gray-400">/{c.totalAll}</span></p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">documents fournis</p>
          </div>
          {headerAction}
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
          <span>Documents obligatoires</span>
          <span>{c.provided}/{c.total} • {pct}%</span>
        </div>
        <div className="h-1.5 bg-gray-200 w-full">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      {useDocs && (counts.accepted || counts.rejected || counts.pending || counts.missing > 0) && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="compliance-status-counters">
          {counts.accepted > 0 && <span className="text-[10px] px-2 py-0.5 border rounded-none bg-green-50 border-green-300 text-green-700 font-bold uppercase">✓ {counts.accepted} accepté{counts.accepted > 1 ? "s" : ""}</span>}
          {counts.rejected > 0 && <span className="text-[10px] px-2 py-0.5 border rounded-none bg-red-50 border-red-300 text-red-700 font-bold uppercase">✗ {counts.rejected} refusé{counts.rejected > 1 ? "s" : ""}</span>}
          {counts.pending > 0 && <span className="text-[10px] px-2 py-0.5 border rounded-none bg-amber-50 border-amber-300 text-amber-700 font-bold uppercase">⏱ {counts.pending} en attente</span>}
          {counts.missing > 0 && <span className="text-[10px] px-2 py-0.5 border rounded-none bg-gray-50 border-gray-200 text-gray-500 font-bold uppercase">{counts.missing} manquant{counts.missing > 1 ? "s" : ""}</span>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        {items.map((d) => {
          const Icon = d.icon;
          const testKey = Array.isArray(d.key) ? d.key[0] : d.key;
          if (useDocs) {
            const { status, doc } = statusForDocEntry(visibleDocs, d);
            const style = STATUS_STYLE[status];
            const rejReason = status === "rejected" ? (doc?.validation_comment || "") : "";
            const canClick = status === "missing" ? !!onRequestUpload : !!onPreviewDoc;
            const hasDoc = status !== "missing" && !!doc;
            const canRename = hasDoc && !!onRenameDoc;
            const canArchive = hasDoc && !!onArchiveDoc;
            const canDelete = hasDoc && !!onDeleteDoc;
            const handleClick = () => {
              if (status === "missing") { onRequestUpload?.(Array.isArray(d.key) ? d.key[0] : d.key); }
              else if (doc) { onPreviewDoc?.(doc); }
            };
            return (
              <div
                key={testKey}
                className={`relative flex items-stretch border ${style.cls}`}
                data-testid={`compliance-item-${testKey}`}
                data-status={status}
              >
                <button
                  type="button"
                  title={rejReason ? `Motif de refus : ${rejReason}` : (status === "missing" ? `Déposer un ${d.label.toLowerCase()}` : `Prévisualiser ${d.label.toLowerCase()}`)}
                  onClick={canClick ? handleClick : undefined}
                  className={`flex items-center gap-2 p-2 text-left flex-1 min-w-0 ${canClick ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
                >
                  <style.Icon className={`w-4 h-4 shrink-0 ${style.iconCls}`} />
                  <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="text-xs font-semibold truncate flex-1">{d.label}</span>
                  {d.required && <span className="text-[9px] uppercase font-bold text-[#662483]">obl.</span>}
                  {d.adminOnly && <span className="text-[9px] uppercase font-bold text-amber-700" title="Admin uniquement">admin</span>}
                  <span className="text-[9px] uppercase font-bold shrink-0">{style.label}</span>
                </button>
                {canRename && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRenameDoc(doc); }}
                    title={`Renommer / recatégoriser « ${doc.original_filename || "document"} »`}
                    className="border-l border-current/20 px-2 flex items-center justify-center hover:bg-white/60 shrink-0"
                    data-testid={`compliance-rename-${testKey}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {canArchive && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onArchiveDoc(doc); }}
                    title="Archiver ce document (masqué mais restaurable)"
                    className="border-l border-current/20 px-2 flex items-center justify-center hover:bg-white/60 shrink-0"
                    data-testid={`compliance-archive-${testKey}`}
                  >
                    <Archive className="w-3.5 h-3.5" />
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDeleteDoc(doc); }}
                    title="Supprimer définitivement ce document"
                    className="border-l border-current/20 px-2 flex items-center justify-center hover:bg-white/60 text-red-700 shrink-0"
                    data-testid={`compliance-delete-${testKey}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          }
          // Legacy mode : only presence-check
          const has = Array.isArray(d.key) ? d.key.some((k) => set.has(k)) : set.has(d.key);
          return (
            <div key={testKey} data-testid={`compliance-item-${testKey}`} className="flex items-center gap-2 bg-white border border-gray-100 p-2">
              {has ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <XCircle className="w-4 h-4 text-gray-300 shrink-0" />}
              <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className={`text-xs ${has ? "text-[#3F3F3F]" : "text-gray-400"} truncate flex-1`}>{d.label}</span>
              {d.required && <span className="text-[9px] uppercase font-bold text-[#662483]">obl.</span>}
            </div>
          );
        })}
      </div>
      {c.missing.length > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">
          ⚠️ Manquant : <span className="font-semibold">{c.missing.join(", ")}</span>
        </p>
      )}
    </div>
  );
}
